import { getEngagementsBaseUrl } from "src/config/Endpoints";
import { getTenantId } from "src/utils/getters";
import i18n from "src/i18n/config";
import { getApiLangFromI18n, normalizeApiLang } from "src/utils/apiLang";
import { buildAuthHeaders, handleJsonResponse } from "./HttpUtils";

const ENGAGEMENTS_BASE_URL = getEngagementsBaseUrl();

export type EngagementMedium = "email" | "whatsapp";

export type EngagementChange = number | "-";

export type EngagementCampaignFilter = string | string[] | null;

interface EngagementEnvelope<TData> {
  status?: string;
  data: TData;
}

export interface EngagementPeriod {
  start_date: string;
  end_date: string;
}

export interface EngagementRangeRequest {
  start_date: string;
  end_date: string;
}

export interface EngagementCampaignNamesRequest extends EngagementRangeRequest {
  medium: EngagementMedium | null;
}

export interface EngagementCampaignNamesData {
  start_date: string;
  end_date: string;
  medium: EngagementMedium | null;
  total_campaign_names: number;
  campaign_names: string[];
}

export interface EngagementFilteredRequest extends EngagementRangeRequest {
  campaign_name: EngagementCampaignFilter;
}

export interface EngagementAchievementMetric {
  value: number | null;
  previous_value?: number | null;
  percent_change?: EngagementChange | null;
  formattedValue?: string | null;
  valueFormattedValue?: string | null;
  previousFormattedValue?: string | null;
  previous_valueFormattedValue?: string | null;
  percent_changeFormattedValue?: string | null;
}

export interface EngagementAchievementMoneyMetric extends EngagementAchievementMetric {}

export interface EngagementAchievementsData {
  currency?: string | null;
  period?: EngagementPeriod | null;
  comparison_period?: EngagementPeriod | null;
  total_orders?: EngagementAchievementMetric | null;
  total_sales?: EngagementAchievementMoneyMetric | null;
  total_users?: EngagementAchievementMetric | null;
  total_customers?: EngagementAchievementMetric | null;
}

export interface EngagementEmailKpisRequest extends EngagementFilteredRequest {
  refresh?: boolean;
}

export interface EngagementEmailKpisData {
  start_date: string;
  end_date: string;
  comparison_period?: EngagementPeriod | null;

  sent: number;
  opened: number;
  clicks: number;
  conversions: number;
  total_users: number;
  open_rate: number;
  click_through_rate: number;
  bounce_rate: number;

  sentFormattedValue?: string | null;
  openedFormattedValue?: string | null;
  clicksFormattedValue?: string | null;
  conversionsFormattedValue?: string | null;
  total_usersFormattedValue?: string | null;
  open_rateFormattedValue?: string | null;
  click_through_rateFormattedValue?: string | null;
  bounce_rateFormattedValue?: string | null;

  sent_change?: EngagementChange | null;
  opened_change?: EngagementChange | null;
  clicks_change?: EngagementChange | null;
  conversions_change?: EngagementChange | null;
  total_users_change?: EngagementChange | null;
  open_rate_change?: EngagementChange | null;
  click_through_rate_change?: EngagementChange | null;
  bounce_rate_change?: EngagementChange | null;

  sent_changeFormattedValue?: string | null;
  opened_changeFormattedValue?: string | null;
  clicks_changeFormattedValue?: string | null;
  conversions_changeFormattedValue?: string | null;
  total_users_changeFormattedValue?: string | null;
  open_rate_changeFormattedValue?: string | null;
  click_through_rate_changeFormattedValue?: string | null;
  bounce_rate_changeFormattedValue?: string | null;
}

export interface EngagementWhatsAppKpisData {
  start_date: string;
  end_date: string;
  comparison_period?: EngagementPeriod | null;

  sent: number;
  delivery_rate: number;
  read_rate: number;
  click_rate: number;
  reply_rate: number;
  conversion_rate: number;
  block_rate: number;
  bounce_rate: number;

  sentFormattedValue?: string | null;
  delivery_rateFormattedValue?: string | null;
  read_rateFormattedValue?: string | null;
  click_rateFormattedValue?: string | null;
  reply_rateFormattedValue?: string | null;
  conversion_rateFormattedValue?: string | null;
  block_rateFormattedValue?: string | null;
  bounce_rateFormattedValue?: string | null;

  sent_change?: EngagementChange | null;
  delivery_rate_change?: EngagementChange | null;
  read_rate_change?: EngagementChange | null;
  click_rate_change?: EngagementChange | null;
  reply_rate_change?: EngagementChange | null;
  conversion_rate_change?: EngagementChange | null;
  block_rate_change?: EngagementChange | null;
  bounce_rate_change?: EngagementChange | null;

  sent_changeFormattedValue?: string | null;
  delivery_rate_changeFormattedValue?: string | null;
  read_rate_changeFormattedValue?: string | null;
  click_rate_changeFormattedValue?: string | null;
  reply_rate_changeFormattedValue?: string | null;
  conversion_rate_changeFormattedValue?: string | null;
  block_rate_changeFormattedValue?: string | null;
  bounce_rate_changeFormattedValue?: string | null;
}

export interface EngagementMediumRequest extends EngagementFilteredRequest {
  medium: EngagementMedium | null;
  refresh?: boolean;
}

export interface EngagementCampaignPerformanceRow {
  campaign_id: string;
  campaign_name: string;
  medium: EngagementMedium | null;
  sent: number;
  open_rate: number;
  click_rate: number;
  click_through_rate: number;
  sentFormattedValue?: string | null;
  open_rateFormattedValue?: string | null;
  click_rateFormattedValue?: string | null;
  click_through_rateFormattedValue?: string | null;
}

export interface EngagementCampaignPerformanceData {
  start_date: string;
  end_date: string;
  medium: EngagementMedium | null;
  total_campaigns: number;
  campaigns: EngagementCampaignPerformanceRow[];
}

export interface EngagementTimeSeriesRow {
  date: string;
  medium: EngagementMedium | null;
  sent: number;
  opened: number;
  clicks: number;
  open_rate: number;
  click_rate: number;
  click_through_rate: number;
  bounce_rate: number;
  dateFormattedValue?: string | null;
  sentFormattedValue?: string | null;
  openedFormattedValue?: string | null;
  clicksFormattedValue?: string | null;
  open_rateFormattedValue?: string | null;
  click_rateFormattedValue?: string | null;
  click_through_rateFormattedValue?: string | null;
  bounce_rateFormattedValue?: string | null;
}

export interface EngagementTimeSeriesData {
  start_date: string;
  end_date: string;
  medium: EngagementMedium | null;
  series: EngagementTimeSeriesRow[];
}

const postEngagement = async <TData,>(
  path: string,
  body: Record<string, unknown>,
): Promise<TData> => {
  const params = new URLSearchParams({
    lang: normalizeApiLang(getApiLangFromI18n(i18n)),
  });
  const response = await fetch(`${ENGAGEMENTS_BASE_URL}/${path}?${params.toString()}`, {
    method: "POST",
    headers: buildAuthHeaders("api_key"),
    body: JSON.stringify({ tenant_id: Number(getTenantId()), ...body }),
  });
  const payload = await handleJsonResponse<EngagementEnvelope<TData>>(response);
  return payload.data;
};

export const engagementOverviewService = {
  fetchCampaignNames: (request: EngagementCampaignNamesRequest) =>
    postEngagement<EngagementCampaignNamesData>("campaign-names", { ...request }),

  fetchAchievements: (request: EngagementFilteredRequest) =>
    postEngagement<EngagementAchievementsData>("datatram-achievements", { ...request }),

  fetchEmailKpis: ({ refresh = false, ...request }: EngagementEmailKpisRequest) =>
    postEngagement<EngagementEmailKpisData>("overview-email-kpis", { ...request, refresh }),

  fetchWhatsAppKpis: (request: EngagementFilteredRequest) =>
    postEngagement<EngagementWhatsAppKpisData>("overview-whatsapp-kpis", { ...request }),

  fetchCampaignPerformance: ({ refresh = false, ...request }: EngagementMediumRequest) =>
    postEngagement<EngagementCampaignPerformanceData>("campaign-performance", {
      ...request,
      refresh,
    }),

  fetchTimeSeries: ({ refresh = false, ...request }: EngagementMediumRequest) =>
    postEngagement<EngagementTimeSeriesData>("time-series", { ...request, refresh }),
};
